import React, { useState } from 'react';
import {
  X,
  Plus,
  Minus,
  Trash2,
  ShoppingBag,
  CreditCard
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Card, CardContent } from '@/components/ui/card';
import { useCart } from '@/contexts/CartContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ImageManager } from '@/lib/imageManager';
import { GroupBuyTooltipBadge } from '@/components/groupbuy/GroupBuyTooltipBadge';
import { CartItemDisplay } from './cart/CartItemDisplay';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";

interface CartContentProps {
  items: any[];
  selectedItems: Set<string>;
  toggleItemSelection: (id: string) => void;
  handleSelectAll: () => void;
  confirmClearCart: boolean;
  setConfirmClearCart: (open: boolean) => void;
  handleClearCart: () => void;
  handleRemoveItem: (id: string) => void;
  incrementQuantity: (id: string) => void;
  decrementQuantity: (id: string) => void;
  closeCart: () => void;
  navigate: (path: string) => void;
  handleCheckout: () => void;
  formatPrice: (price: number) => string;
  isDrawer?: boolean;
}

const CartContent: React.FC<CartContentProps> = ({
  items,
  selectedItems,
  toggleItemSelection,
  handleSelectAll,
  confirmClearCart,
  setConfirmClearCart,
  handleClearCart,
  handleRemoveItem,
  incrementQuantity,
  decrementQuantity,
  closeCart,
  navigate,
  handleCheckout,
  formatPrice,
  isDrawer = false
}) => {
  const selectedItemsCount = items.filter(item => selectedItems.has(item.id)).length;

  return (
    <div className="flex flex-col h-full" dir="rtl">
      <div className="flex-shrink-0 bg-background border-b border-border p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <ShoppingBag className="h-6 w-6 text-lime" />
            <h2 className="text-xl font-bold text-foreground">עגלת קניות</h2>
            {items.length > 0 && (
              <span className="text-sm text-muted-foreground">
                {items.length} מוצרים • {selectedItemsCount} נבחרו
              </span>
            )}
          </div>
          {!isDrawer && (
            <Button variant="ghost" size="sm" onClick={closeCart} className="p-2">
              <X className="h-6 w-6" />
            </Button>
          )}
        </div>

        {items.length > 0 && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={selectedItemsCount === items.length && items.length > 0}
                onCheckedChange={handleSelectAll}
              />
              <span className="text-sm font-medium">בחר הכל ({items.length} פריטים)</span>
            </div>

            {items.length > 1 && (
              <AlertDialog open={confirmClearCart} onOpenChange={setConfirmClearCart}>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    נקה עגלה
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent dir="rtl" className="z-[10000]">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-right">ריקון עגלת הקניות</AlertDialogTitle>
                    <AlertDialogDescription className="text-right">
                      האם אתה בטוח שברצונך להסיר את כל המוצרים מהעגלה? פעולה זו לא ניתנת לביטול.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter className="flex gap-2 justify-start">
                    <AlertDialogCancel>ביטול</AlertDialogCancel>
                    <AlertDialogAction onClick={handleClearCart} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                      רוקן עגלה
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
          <ShoppingBag className="h-16 w-16 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">העגלה ריקה</h3>
          <p className="text-muted-foreground mb-6">הוסיפו מוצרים כדי להתחיל</p>
          <Button
            className="bg-lime hover:bg-lime/90 text-lime-foreground"
            onClick={() => {
              closeCart();
              navigate('/products');
            }}
          >
            התחל לקנות
          </Button>
        </div>
      ) : (
        <>
          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-3">
              {items.map((item) => (
                <CartItemDisplay
                  key={item.id}
                  item={item}
                  isSelected={selectedItems.has(item.id)}
                  onToggleSelection={toggleItemSelection}
                  onRemove={(id) => handleRemoveItem(id)}
                  onIncrement={incrementQuantity}
                  onDecrement={decrementQuantity}
                  closeCart={closeCart}
                  isSidebar={true}
                />
              ))}
            </div>
          </div>

          <div className="flex-shrink-0 bg-background p-4">
            <div className="space-y-3" dir="rtl">
              <div className="text-center text-xs space-y-1">
                {(() => {
                  const selectedTotal = items.filter(item => selectedItems.has(item.id)).reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);
                  if (selectedTotal < 1500) {
                    return <p className="text-orange-600 font-medium">מינימום למשלוח ₪1,500 (איסוף עצמי בלבד בסכום הנוכחי)</p>;
                  }
                  return null;
                })()}
              </div>

              <Button
                className="w-full bg-lime hover:bg-lime/90 text-lime-foreground"
                onClick={handleCheckout}
              >
                המשך לתשלום ({formatPrice(items.filter(item => selectedItems.has(item.id)).reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0))})
                <span className="text-xs opacity-75 mr-1">כולל מע"מ</span>
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const CartSidebar: React.FC = () => {
  const {
    items,
    isOpen,
    closeCart,
    removeItem,
    updateQuantity,
    incrementQuantity,
    decrementQuantity,
    clearCart,
    toggleItemSelection,
    selectAllItems,
    selectedItems,
    getSelectedItemsCount,
    isCheckoutActive,
  } = useCart();

  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [editingQuantity, setEditingQuantity] = useState<string | null>(null);
  const [tempQuantity, setTempQuantity] = useState<string>('');
  const [confirmClearCart, setConfirmClearCart] = useState(false);
  const [confirmRemoveItem, setConfirmRemoveItem] = useState<string | null>(null);

  const handleRemoveItem = (id: string) => {
    removeItem(id);
    setConfirmRemoveItem(null);
  };

  const handleQuantityChange = (id: string, newQuantity: number) => {
    const item = items.find(item => item.id === id);
    if (!item) return;

    const minQty = item.moq;

    // For group buy products, don't apply inventory-based max limit
    const isGroupBuy = item.product.group_buy_enabled;
    const maxQty = isGroupBuy
      ? (item.maxOrderQuantity || Number.MAX_SAFE_INTEGER)
      : Math.min(
        item.maxOrderQuantity || item.product.availableQuantity,
        item.product.availableQuantity
      );

    if (newQuantity < minQty) {
      toast.error("כמות לא תקינה", {
        description: `הכמות המינימלית להזמנה היא ${minQty} יח'`,
      });
      return;
    }

    if (newQuantity > maxQty && !isGroupBuy) {
      toast.error("כמות לא תקינה", {
        description: `הכמות המקסימלית להזמנה היא ${maxQty} יח'`,
      });
      return;
    }

    updateQuantity(id, newQuantity);
  };

  const handleSelectAll = () => {
    const allSelected = selectedItems.size === items.length && items.length > 0;
    if (allSelected) {
      items.forEach(item => {
        if (selectedItems.has(item.id)) {
          toggleItemSelection(item.id);
        }
      });
    } else {
      selectAllItems();
    }
  };

  const handleCheckout = () => {
    if (getSelectedItemsCount() === 0) {
      toast.error("בחר פריטים", {
        description: "אנא בחר לפחות פריט אחד כדי להמשיך לתשלום",
      });
      return;
    }

    closeCart();
    navigate('/checkout');
  };

  const handleClearCart = () => {
    clearCart();
    setConfirmClearCart(false);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(price);
  };

  if (!isOpen || isCheckoutActive) {
    return null;
  }

  const commonProps = {
    items,
    selectedItems,
    toggleItemSelection,
    handleSelectAll,
    confirmClearCart,
    setConfirmClearCart,
    handleClearCart,
    handleRemoveItem,
    incrementQuantity,
    decrementQuantity,
    closeCart,
    navigate,
    handleCheckout,
    formatPrice
  };

  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={(open) => !open && closeCart()}>
        <DrawerContent className="h-[90vh] flex flex-col mt-24 max-h-[96vh]">
          <DrawerHeader className="hidden">
            <DrawerTitle>עגלת קניות</DrawerTitle>
            <DrawerDescription>עגלת קניות</DrawerDescription>
          </DrawerHeader>
          <CartContent {...commonProps} isDrawer={true} />
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <>
      <div
        className="fixed inset-0 bg-black/60 transition-opacity duration-300"
        style={{ zIndex: 9998 }}
        onClick={closeCart}
      />

      <div
        className="fixed top-0 right-0 h-full w-full sm:w-[28rem] lg:w-[32rem] bg-background shadow-2xl transform transition-transform duration-300 ease-in-out"
        style={{ zIndex: 9999 }}
        dir="rtl"
      >
        <CartContent {...commonProps} isDrawer={false} />
      </div>
    </>
  );
};

export default CartSidebar;
